import { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import BasePageLayout from '../../../components/layouts/base-page-layout/base-page-layout';
import Card from '../../../components/layouts/card/card';
import pagedListStore from './credits-paged-list-page-store';
import Table from '../../../components/table/table';
import TableRow from '../../../components/table/components/table-row/table-row';
import portalStore from '../../../stores/current-portal-store';
import Amount from '../components/amount/amount';
import AHref from '../../../components/ahref/ahref';
import SearchInput from '../../../components/search-input/search-input';
import SearchBar from '../../../components/layouts/search-bar/search-bar';
import FontBody2 from '../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR } from '../../../components/fonts/font-props';
import AdjustCreditButton from './components/adjust-credit-button/adjust-credit-button';
import AdjustCreditBalanceModal from './components/adjust-credit-balance-modal/adjust-credit-balance-modal';
import CreditSrore from './credit-store';
import FontBody1 from '../../../components/fonts/font-body1/font-body1';
import Report from '../../../components/report/report';
import ReportBoxLayout from '../../../components/layouts/report-box-layout/report-box-layout';
import { REPORT_TYPES } from '../../../services/api/report-service';
import { useSearchParams } from "react-router-dom";

interface ICreditsPagedListPageProps { }

const CreditsPagedListPage: React.FC<ICreditsPagedListPageProps> = observer(() => {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        pagedListStore.setSearchCustomerId(searchParams.get('search_customer_id') ?? '');
        searchParams.delete('search_customer_id');

        pagedListStore.loadPage(1);
        portalStore.load();
    }, [searchParams]);

    const onSearchClick = useCallback(() => pagedListStore.loadPage(1), []);

    const onSearchChange = useCallback((searchInput: string) => {
        pagedListStore.setSearchCustomerId(searchInput);
    }, []);

    return (
        <BasePageLayout
            title="Analytics"
            subTitles={['Credits']}
            instruction={<>
                <div>
                    <FontBody1>
                        View your customers’ current store credit balance. 
                    </FontBody1>
                </div>
                <div>
                    <FontBody1>
                        For privacy reasons, only customer IDs are displayed.
                    </FontBody1>
                </div>
            </>}
        >
            <ReportBoxLayout>
                <Report
                    type={REPORT_TYPES.CREDITS}
                    title='report'
                />
            </ReportBoxLayout>

            <Card>
                <SearchBar>
                    <SearchInput
                        value={pagedListStore.searchCustomerId ?? ''}
                        onClick={onSearchClick}
                        onChange={onSearchChange}
                        tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Search by customer ID.</FontBody2>}
                    />
                </SearchBar>
                <Table
                    isLoading={pagedListStore.isLoading}
                    pagination={{
                        onSetPage: pagedListStore.loadPage,
                        page: pagedListStore.page,
                        total: pagedListStore.total,
                        perPage: pagedListStore.perPage
                    }}
                    sort={{
                        onSort: pagedListStore.sortData,
                        by: pagedListStore.sortBy,
                        dir: pagedListStore.sortDir,
                    }}
                    columns={[
                        {
                            content: 'id',
                            sortBy: 'id',
                            sortable: true,
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'customer id',
                            width: {
                                value: 3,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'discount code',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'credits',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'gift cards',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'actions',
                            width: {
                                value: 50,
                                type: 'px'
                            }
                        }
                    ]}
                    totalResultsTitle='Credits'
                >
                    {pagedListStore.list.map((item: CreditSrore) => (
                        <TableRow
                            key={item.id}
                            cells={[
                                item.id,
                                <AHref
                                    target='_blank'
                                    href={item.admin_customer_url}
                                >
                                    {item.store_customer_id}
                                </AHref>,
                                <AHref
                                    target='_blank'
                                    href={item.admin_discounts_url}
                                >
                                    {item.discount_code}
                                </AHref>,
                                <Amount value={item.credits_formatted} />,
                                <Amount value={item.gift_card_credits_formatted} />,
                                <AdjustCreditButton onClick={() => pagedListStore.setCreditToAdjust(item.getModel())} />
                            ]}
                        />
                    ))}
                </Table>
            </Card>
            {pagedListStore.creditToAdjust && <AdjustCreditBalanceModal onClose={() => pagedListStore.setCreditToAdjust(undefined)} />}
        </BasePageLayout>
    );
});

export default CreditsPagedListPage;
